import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { AppConfig } from '@app/core/app.config';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { DocumentState } from '@app/core/enums/document/document-state.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { Asset } from '@app/core/model/entities/asset/asset';
import { FIELD_EXTRA_DATA } from '@app/core/model/other/field-config';
import { PicturesService } from '@app/features/main/views/assets/asset-sheet/pictures/pictures.service';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { getPictureComparator } from '@app/shared/extra/utils';
import { FileService } from '@services/file.service';
import { AccessManager } from '@services/managers/access.manager';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'assets-map-sidebar-control',
  templateUrl: './assets-map-sidebar-control.component.html',
  styleUrls: ['./assets-map-sidebar-control.component.scss'],
  standalone: false
})
export class AssetsMapSidebarControlComponent implements OnInit, OnDestroy {
  protected readonly appConfig = inject(AppConfig);
  private readonly accessManager = inject(AccessManager);
  private readonly assetsService = inject(AssetsService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly pictureService = inject(PicturesService);
  private readonly fileService = inject(FileService);


  public Permission = PermissionEnum;

  public asset = inject<{ asset: Asset }>(FIELD_EXTRA_DATA).asset;

  public imageSource: string;
  public tabs: {
    entityType: EntityTypeEnum,
    name: string,
    permission: PermissionEnum,
    key: string,
    conditionToView: boolean
  }[] = [];

  private destroyRef = inject(DestroyRef);

  constructor() {
    // Build the list of links
    const assetTypeModules: string[] = this.asset.computedProperties?.['typeModules'] ?? [];
    this.tabs = [
      {
        entityType: EntityTypeEnum.ASSET,
        name: 'MENU.ASSET_DASHBOARD',
        permission: PermissionEnum.VIEW_ASSET,
        key: 'dashboard',
        conditionToView: true
      },
      {
        entityType: EntityTypeEnum.ASSET,
        name: 'MENU.ASSET_FEATURES',
        permission: PermissionEnum.VIEW_ASSET,
        key: 'features',
        conditionToView: true
      },
      {
        entityType: EntityTypeEnum.ASSET,
        name: 'MENU.ASSET_INDICATORS',
        permission: PermissionEnum.VIEW_ASSET,
        key: 'indicators',
        conditionToView: true
      },
      {
        entityType: EntityTypeEnum.CRV,
        name: 'MENU.ASSET_CRV',
        permission: PermissionEnum.VIEW_CRV,
        key: 'crv',
        conditionToView: assetTypeModules.includes(EntityTypeEnum.ASSET_CRV)
      },
      {
        entityType: EntityTypeEnum.SPACE,
        name: 'MENU.ASSET_SPACES',
        permission: PermissionEnum.VIEW_SPACE,
        key: 'spaces',
        conditionToView: assetTypeModules.includes(EntityTypeEnum.SPACE)
      },
      {
        entityType: EntityTypeEnum.EQUIPMENT,
        name: 'MENU.ASSET_EQUIPMENTS',
        permission: PermissionEnum.VIEW_EQUIPMENT,
        key: 'equipments',
        conditionToView: assetTypeModules.includes(EntityTypeEnum.EQUIPMENT)
      },
      {
        entityType: EntityTypeEnum.ASSET,
        name: 'MENU.ASSET_PICTURES',
        permission: PermissionEnum.VIEW_ASSET,
        key: 'pictures',
        conditionToView: true
      },
      {
        entityType: EntityTypeEnum.DOCUMENT,
        name: 'MENU.ASSET_DOCUMENTS',
        permission: PermissionEnum.VIEW_DOCUMENT,
        key: 'documents',
        conditionToView: true
      },
      {
        entityType: EntityTypeEnum.CHECK,
        name: 'MENU.ASSET_CHECKS',
        permission: PermissionEnum.VIEW_CHECK,
        key: 'checks',
        conditionToView: assetTypeModules.includes(EntityTypeEnum.CHECK)
      },
      {
        entityType: EntityTypeEnum.OCCUPANT,
        name: 'MENU.ASSET_OCCUPANTS',
        permission: PermissionEnum.VIEW_OCCUPANT,
        key: 'occupants',
        conditionToView: assetTypeModules.includes(EntityTypeEnum.LEASE)
      },
      {
        entityType: EntityTypeEnum.CONTRACT,
        name: 'MENU.ASSET_CONTRACTS',
        permission: PermissionEnum.VIEW_CONTRACT,
        key: 'contracts',
        conditionToView: assetTypeModules.includes(EntityTypeEnum.CONTRACT)
      },
      {
        entityType: EntityTypeEnum.WORK,
        name: 'MENU.ASSET_WORKS',
        permission: PermissionEnum.VIEW_WORK,
        key: 'works',
        conditionToView: assetTypeModules.includes(EntityTypeEnum.WORK)
      },
      {
        entityType: EntityTypeEnum.PROJECT,
        name: 'MENU.ASSET_PROJECTS',
        permission: PermissionEnum.VIEW_PROJECT,
        key: 'projects',
        conditionToView: assetTypeModules.includes(EntityTypeEnum.PROJECT)
      },
      {
        entityType: EntityTypeEnum.VIRTUAL_TOUR,
        name: 'MENU.ASSET_VIRTUAL_TOUR',
        permission: PermissionEnum.VIEW_VIRTUAL_TOUR,
        key: 'virtual-tour',
        conditionToView: assetTypeModules.includes(EntityTypeEnum.VIRTUAL_TOUR)
      }
    ]
      // Remove tabs which the user can't access
      .filter(tab => this.accessManager.hasAccess(tab.permission) && tab.conditionToView);
  }

  public ngOnInit(): void {
    this.loadAssetPicture(this.asset.id).subscribe(() => this.changeDetectorRef.detectChanges());
  }

  /**
   * Load the asset's picture
   * @param assetId
   * @return An observable emitting when the request complete
   */
  private loadAssetPicture(assetId: string): Observable<any> {
    return this.pictureService.loadData(assetId).pipe(
      switchMap(pictures => {
        const picture = pictures.filter(picture => picture.state === DocumentState.ACTIVE)
          .sort(getPictureComparator(this.asset.properties['mainPictureDocumentId']))
          .first();

        if (picture) {
          const documentId = picture.id;
          return this.fileService.getDocumentFileUrls([documentId])
            .pipe(map(urls => this.imageSource = urls[picture.id]));
        }
        return of(null);
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  /**
   * Navigate to the asset sheet tab.
   * @param key the tab to navigate to.
   */
  public async navigateToAssetSheet(key: string): Promise<void> {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.SIDEPANEL,
      NavigateToEnum.SHEET,
      EntityTypeEnum.ASSET,
      this.asset.id
    );
    await this.assetsService.navigateToAssetSheet(this.asset.id, key);
  }

  public ngOnDestroy(): void {
    this.changeDetectorRef.detach();
  }
}
